import { Assessment, FAScores, AssessmentType } from '../types';
import { api } from './api';
import { MOCK_ASSESSMENTS, MOCK_PLAYERS, MOCK_TEAMS, hasAccessToPreSeededData, getCurrentMockUserId, saveMockAssessments } from './mockData';

interface CreateAssessmentRequest {
  playerId: string;
  type: 'INITIAL' | 'FOLLOW_UP';
  scores: FAScores;
  notes?: string;
  isComplete?: boolean;
}

const isDevelopmentMode = process.env.NODE_ENV === 'development';

export const assessmentService = {
  async getPlayerAssessments(playerId: string): Promise<Assessment[]> {
    try {
      const response = await api.get<{ data: Assessment[] }>(`/assessments/player/${playerId}`);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock assessment data for development');
        
        const playerAssessments = MOCK_ASSESSMENTS.filter((assessment: Assessment) => assessment.playerId === playerId);
        
        // If not the original mock coach, only show assessments for players they have access to
        if (!hasAccessToPreSeededData()) {
          // Check if this player belongs to a team the user has access to
          const player = MOCK_PLAYERS.find((p: any) => p.id === playerId);
          if (player) {
            const team = MOCK_TEAMS.find((t: any) => t.id === player.teamId);
            if (team && !team.coachId.startsWith('mock-')) {
              return []; // No access to pre-seeded assessments
            }
          }
        }
        
        return playerAssessments;
      }
      throw error;
    }
  },

  async createAssessment(data: CreateAssessmentRequest): Promise<Assessment> {
    try {
      const response = await api.post<{ data: Assessment }>('/assessments', data);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock assessment creation for development');
        
        // Create mock assessment
        const mockAssessment: Assessment = {
          id: `mock-assessment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          playerId: data.playerId,
          assessorId: getCurrentMockUserId(),
          type: data.type === 'INITIAL' ? AssessmentType.INITIAL : AssessmentType.PERIODIC,
          scores: data.scores,
          notes: data.notes,
          isComplete: data.isComplete ?? true,
          assessmentDate: new Date(),
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        // Add to mock data for this session
        MOCK_ASSESSMENTS.push(mockAssessment);
        saveMockAssessments();
        
        return mockAssessment;
      }
      throw error;
    }
  },

  async updateAssessment(assessmentId: string, data: Partial<CreateAssessmentRequest>): Promise<Assessment> {
    try {
      const response = await api.put<{ data: Assessment }>(`/assessments/${assessmentId}`, data);
      return response.data.data;
    } catch (error: any) {
      const isConnectionError = isDevelopmentMode && (
        error.code === 'ERR_NETWORK' || 
        error.code === 'ERR_CONNECTION_REFUSED' ||
        error.code === 'ECONNREFUSED' ||
        error.code === 'ECONNABORTED' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('timeout') ||
        !error.response
      );
      
      if (isConnectionError) {
        console.warn('🔧 Backend not available, using mock assessment update for development');
        
        // Find the assessment in mock data
        const assessmentIndex = MOCK_ASSESSMENTS.findIndex((assessment: Assessment) => assessment.id === assessmentId);
        if (assessmentIndex === -1) {
          throw new Error('Assessment not found');
        }
        
        const assessment = MOCK_ASSESSMENTS[assessmentIndex];
        
        // Check if user has access to update this assessment (via player/team ownership)
        if (!hasAccessToPreSeededData()) {
          const player = MOCK_PLAYERS.find((p: any) => p.id === assessment.playerId);
          if (player) {
            const team = MOCK_TEAMS.find((t: any) => t.id === player.teamId);
            if (team && !team.coachId.startsWith('mock-')) {
              throw new Error('Access denied');
            }
          }
        }
        
        // Update the assessment
        const updatedAssessment = {
          ...assessment,
          ...data,
          type: data.type === 'INITIAL' ? AssessmentType.INITIAL : 
                data.type === 'FOLLOW_UP' ? AssessmentType.PERIODIC : assessment.type,
          updatedAt: new Date()
        };
        
        MOCK_ASSESSMENTS[assessmentIndex] = updatedAssessment;
        return updatedAssessment;
      }
      throw error;
    }
  },

  async getAssessment(assessmentId: string): Promise<Assessment> {
    const response = await api.get<{ data: Assessment }>(`/assessments/${assessmentId}`);
    return response.data.data;
  },

  async getDueAssessments(): Promise<Assessment[]> {
    const response = await api.get<{ data: Assessment[] }>('/assessments/due');
    return response.data.data;
  },

  async getPlayerProgress(playerId: string): Promise<any> {
    const response = await api.get<{ data: any }>(`/assessments/player/${playerId}/progress`);
    return response.data.data;
  },

  async saveDraft(data: CreateAssessmentRequest): Promise<Assessment> {
    return this.createAssessment({ ...data, isComplete: false });
  },

  async updateDraft(assessmentId: string, data: Partial<CreateAssessmentRequest>): Promise<Assessment> {
    // Filter out fields that shouldn't be updated (playerId, type)
    const { playerId, type, ...updateData } = data;
    return this.updateAssessment(assessmentId, { ...updateData, isComplete: false });
  },

  async completeDraft(assessmentId: string, data: Partial<CreateAssessmentRequest>): Promise<Assessment> {
    // Filter out fields that shouldn't be updated (playerId, type)
    const { playerId, type, ...updateData } = data;
    return this.updateAssessment(assessmentId, { ...updateData, isComplete: true });
  },

  async getDraftAssessment(playerId: string): Promise<Assessment | null> {
    try {
      const assessments = await this.getPlayerAssessments(playerId);
      return assessments.find(assessment => !assessment.isComplete) || null;
    } catch (error) {
      console.error('Failed to get draft assessment:', error);
      return null;
    }
  },

  async updateAssessmentScheduleForTeam(teamId: string, newIntervalWeeks: number): Promise<void> {
    try {
      // This would typically be handled by the backend
      // For now, we'll implement basic logic for development mode
      console.log(`Assessment interval updated for team ${teamId} to ${newIntervalWeeks} weeks`);
      
      // In a real implementation, this would:
      // 1. Get all players in the team
      // 2. Calculate new due dates based on their last assessment + new interval
      // 3. Update any scheduled notifications
      // 4. Reschedule assessment reminders
      
      // For development, we'll just log the change
      console.log('Assessment schedules will be updated based on the new interval');
    } catch (error) {
      console.error('Failed to update assessment schedule:', error);
      throw error;
    }
  },

  calculateNextAssessmentDate(lastAssessmentDate: Date, intervalWeeks: number): Date {
    const nextDate = new Date(lastAssessmentDate);
    nextDate.setDate(nextDate.getDate() + (intervalWeeks * 7));
    return nextDate;
  },

  isAssessmentDue(lastAssessmentDate: Date, intervalWeeks: number): boolean {
    const nextDueDate = this.calculateNextAssessmentDate(lastAssessmentDate, intervalWeeks);
    return new Date() >= nextDueDate;
  },
};